import { Title } from "components/commons";
import { ModuleWrapper } from "components/fragments";
import React, { useCallback, useContext, useMemo, useState } from "react";
import lang from "translations";
import { StaffContext, VenueContext } from "contexts";
import DashboardFilter from "./dashboard-filters";
import { measureTime } from "services";
import { useApi, useMount } from "hooks";
import { mixpanel, TrackEvent } from "mixpanel";
import {
  getGraphReportOfTotalSalesOfLocation,
  getGraphReportOfTotalSales,
  getTopLocationsBySales,
  getGraphOfTopUpSales,
  getGraphOfReturnCredits,
  getTopProductsBySales,
  getAverageOrderValue,
  getTotalTransactionsValue,
  getTopSpendingGuest,
  getTopCategoriesBySales,
} from "apis/transaction.api";
import { searchProductsOutOfStock } from "apis/product.api";
import { getGraphOfActivatedGuest } from "apis/guest.api";
import { getOnboardingChecklist, closeOnboardingBanner } from "apis";
import DashboardCharts from "./dashboard-charts";
import {
  graphLineChartOfTotalSale,
  graphPieChartOfTopLocationSales,
  graphMultiLineChartOfTotalSale,
  topProduct,
  guestWithTotalSpend,
  topCategory,
  outOfStockProduct,
  graphTopUpTotal,
  graphLineChartOfTotalCheckIns,
  graphLineChartOfTotalReturns,
  checklistRequest,
} from "mappers";
import { toApiDateTimeFormat } from "services";
import DashboardOnboarding from "./dashboard-onboarding";
import { useFlags } from "launchdarkly-react-client-sdk";

const Dashboard = () => {
  const { venue } = useContext(VenueContext);
  const { staff } = useContext(StaffContext);
  const [hasLocations, setHasLocations] = useState(false);
  const [params, setParams] = useState({});
  const [displayOnboarding, setDisplayOnboarding] = useState(true);
  const { onboardingFlag } = useFlags();

  const graphOfTotalSales = useApi({
    api: getGraphReportOfTotalSales,
    mapper: graphLineChartOfTotalSale,
    handleOwnError: true,
  });

  const graphOfTotalSalesOfLocation = useApi({
    api: getGraphReportOfTotalSalesOfLocation,
    mapper: graphMultiLineChartOfTotalSale,
    handleOwnError: true,
  });

  const topLocationsBySales = useApi({
    api: getTopLocationsBySales,
    mapper: graphPieChartOfTopLocationSales,
    isArray: true,
    handleOwnError: true,
  });

  const graphOfTopUpSales = useApi({
    api: getGraphOfTopUpSales,
    mapper: graphTopUpTotal,
    handleOwnError: true,
  });

  const graphOfReturnCredits = useApi({
    api: getGraphOfReturnCredits,
    mapper: graphLineChartOfTotalReturns,
    handleOwnError: true,
  });

  const graphOfActivatedGuest = useApi({
    api: getGraphOfActivatedGuest,
    mapper: graphLineChartOfTotalCheckIns,
    handleOwnError: true,
  });

  const topProductsBySales = useApi({
    api: getTopProductsBySales,
    params: {
      limit: 5,
    },
    mapper: topProduct,
    isArray: true,
    handleOwnError: true,
  });

  const averageOrderValue = useApi({
    api: getAverageOrderValue,
    handleOwnError: true,
  });

  const totalTransactionsValue = useApi({
    api: getTotalTransactionsValue,
    handleOwnError: true,
  });

  const topSpendingGuest = useApi({
    api: getTopSpendingGuest,
    params: {
      limit: 5,
    },
    mapper: guestWithTotalSpend,
    isArray: true,
    handleOwnError: true,
  });

  const outOfStockProducts = useApi({
    api: searchProductsOutOfStock,
    mapper: outOfStockProduct,
    isArray: true,
    handleOwnError: true,
  });

  const topCategoriesBySales = useApi({
    api: getTopCategoriesBySales,
    params: {
      limit: 5,
    },
    mapper: topCategory,
    isArray: true,
    handleOwnError: true,
  });

  const {
    request: fetchOnboardingChecklist,
    mappedData: onboardingChecklist,
    loading: onboardingCheklistLoading,
  } = useApi({
    api: getOnboardingChecklist,
    params: {
      venueId: venue.venueId,
    },
    mapper: checklistRequest,
    handleOwnError: true,
  });

  const { request: closeBannerRequest } = useApi({
    api: closeOnboardingBanner,
    params: {
      venueId: venue.venueId,
    },
    handleOwnError: true,
  });

  const fetchCharts = useCallback(
    (filterState) => {
      const { locationIds, dateRange } = filterState;
      const params = {
        ...filterState,
        startDateTime: toApiDateTimeFormat(dateRange[0]),
        endDateTime: toApiDateTimeFormat(dateRange[1]),
        splitType: measureTime([dateRange[0], dateRange[1]]).getSplitType(),
      };
      setParams(params);
      const hasLocationValue = Boolean(locationIds.length);
      setHasLocations(hasLocationValue);
      const charts = [
        graphOfTopUpSales,
        graphOfReturnCredits,
        graphOfActivatedGuest,
        topProductsBySales,
        averageOrderValue,
        totalTransactionsValue,
        topSpendingGuest,
        topCategoriesBySales,
        outOfStockProducts,
      ];

      if (locationIds.length) {
        charts.push(graphOfTotalSalesOfLocation);
      } else {
        charts.push(graphOfTotalSales);
        charts.push(topLocationsBySales);
      }

      charts.forEach((request) => {
        request.request(params);
      });
    },
    [
      graphOfReturnCredits,
      graphOfTopUpSales,
      graphOfTotalSales,
      graphOfTotalSalesOfLocation,
      topLocationsBySales,
      graphOfActivatedGuest,
      topProductsBySales,
      averageOrderValue,
      totalTransactionsValue,
      topSpendingGuest,
      topCategoriesBySales,
      outOfStockProducts,
    ]
  );

  useMount(() => {
    mixpanel.track(TrackEvent.VisitedPage, {
      Page: lang.dashboard,
    });
    fetchOnboardingChecklist();
  });

  const graphs = useMemo(() => {
    const charts = {
      graphOfReturnCredits,
      graphOfTopUpSales,
      graphOfTotalSales,
      graphOfTotalSalesOfLocation,
      topLocationsBySales,
      graphOfActivatedGuest,
      topProductsBySales,
      averageOrderValue,
      totalTransactionsValue,
      topSpendingGuest,
      topCategoriesBySales,
      outOfStockProducts,
    };
    Object.keys(charts).forEach((chart) => {
      charts[chart].retry = () => {
        fetchCharts(params);
      };
    });
    return charts;
  }, [
    graphOfReturnCredits,
    graphOfTopUpSales,
    graphOfTotalSales,
    graphOfTotalSalesOfLocation,
    topLocationsBySales,
    graphOfActivatedGuest,
    topProductsBySales,
    averageOrderValue,
    totalTransactionsValue,
    topSpendingGuest,
    topCategoriesBySales,
    outOfStockProducts,
    params,
    fetchCharts,
  ]);

  return (
    <ModuleWrapper>
      {onboardingFlag && !venue.bannerClosed && displayOnboarding && (
        <DashboardOnboarding
          setDisplay={setDisplayOnboarding}
          onboardingStatus={onboardingChecklist}
          closeBannerRequest={closeBannerRequest}
          loading={onboardingCheklistLoading}
        />
      )}
      <Title className="mt-md font-semibold sm:text-xl">
        {lang.populate(lang.hiHeresWhatsHappening, [staff.name, venue.venueName])}
      </Title>
      <DashboardFilter venueId={venue.venueId} fetchCharts={fetchCharts} />
      <DashboardCharts {...graphs} hasLocations={hasLocations} />
    </ModuleWrapper>
  );
};

export default Dashboard;
